import React, { useState, useEffect } from 'react';
import {
  Users2,
  Search,
  Filter,
  Target,
  Building2,
  Users,
  Plus,
  ArrowRight,
  CheckCircle,
  Clock,
  FileText,
  Zap,
  Link as LinkIcon,
  X,
  ChevronDown,
  Star,
  Award,
  TrendingUp
} from 'lucide-react';
import { useToast } from '../components/Toast';
import { getApiBaseUrl } from '../utils/api';

interface MatchResult {
  id: number;
  company: {
    name: string;
    type: string;
    industry: string;
    successRate: number;
  };
  grant: {
    title: string;
    source: string;
    deadline: string;
    budget: string;
  };
  matchScore: number;
  matchReasons: string[];
  status: '추천' | '검토중' | '컨소시엄구성중' | '제안서작성중';
}

interface Consortium {
  id: number;
  name: string;
  grant: {
    title: string;
    source: string;
    deadline: string;
  };
  leadCompany: string;
  members: {
    name: string;
    role: string;
    contribution: number;
  }[];
  status: '구성중' | '확정' | '제안서작성중' | '제출완료';
  totalBudget: string;
  createdAt: string;
}

const getBaseUrl = () =>
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost:8001'
    : `${window.location.protocol}//${window.location.hostname}:8001`;

const MatchingConsortium: React.FC = () => {
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<'matching' | 'consortium'>('matching');
  const [showCreateConsortium, setShowCreateConsortium] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<MatchResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [matchResults, setMatchResults] = useState<MatchResult[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  const [consortiums, setConsortiums] = useState<Consortium[]>([]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setDataLoading(true);
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const baseUrl = getApiBaseUrl();

        // 매칭 결과 조회
        const matchRes = await fetch(`${baseUrl}/api/matching/results`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal,
        });
        if (matchRes.ok) {
          const data = await matchRes.json();
          const items = Array.isArray(data) ? data : (data.results || []);
          setMatchResults(items);
        }

        // 컨소시엄 목록 조회
        const consRes = await fetch(`${baseUrl}/api/matching/consortiums`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: controller.signal,
        });
        if (consRes.ok) {
          const data = await consRes.json();
          const items = Array.isArray(data) ? data : (data.consortiums || []);
          setConsortiums(items);
        }
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        console.error('Failed to fetch matching data:', err);
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, []);

  const handleRunMatching = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('proposalcraft_access_token');
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/api/matching/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.results) setMatchResults(data.results);
        toast.success(`매칭 완료: ${data.results?.length || 0}건`);
      }
    } catch (err) {
      console.error('Matching failed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case '추천': return 'bg-blue-100 text-blue-800';
      case '검토중': return 'bg-yellow-100 text-yellow-800';
      case '컨소시엄구성중': return 'bg-purple-100 text-purple-800';
      case '제안서작성중': return 'bg-green-100 text-green-800';
      case '구성중': return 'bg-yellow-100 text-yellow-800';
      case '확정': return 'bg-blue-100 text-blue-800';
      case '제출완료': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 80) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-gray-600 bg-gray-100';
  };

  const stats = {
    totalMatches: matchResults.length,
    highMatches: matchResults.filter(m => m.matchScore >= 85).length,
    activeConsortiums: consortiums.filter(c => c.status !== '제출완료').length,
    proposalsInProgress: consortiums.filter(c => c.status === '제안서작성중').length,
  };

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">매칭 & 컨소시엄</h1>
          <p className="text-gray-600 mt-2">AI 기반 기업-공모사업 매칭 및 컨소시엄 구성</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleRunMatching}
            disabled={isLoading}
            className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Zap className="h-5 w-5 mr-2" />
            {isLoading ? '매칭 중...' : 'AI 매칭 실행'}
          </button>
          <button 
            onClick={() => setShowCreateConsortium(true)}
            className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            컨소시엄 구성
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">전체 매칭</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalMatches}</p>
            </div>
            <Target className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">고매칭 (85%+)</p>
              <p className="text-2xl font-bold text-green-600">{stats.highMatches}</p>
            </div>
            <Star className="h-8 w-8 text-green-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">진행 중 컨소시엄</p>
              <p className="text-2xl font-bold text-purple-600">{stats.activeConsortiums}</p>
            </div>
            <Users className="h-8 w-8 text-purple-400" />
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">제안서 작성 중</p>
              <p className="text-2xl font-bold text-orange-600">{stats.proposalsInProgress}</p>
            </div>
            <FileText className="h-8 w-8 text-orange-400" />
          </div>
        </div>
      </div>

      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('matching')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'matching'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Target className="h-5 w-5 inline mr-2" />
            AI 매칭 결과
          </button>
          <button
            onClick={() => setActiveTab('consortium')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'consortium'
                ? 'border-emerald-500 text-emerald-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="h-5 w-5 inline mr-2" />
            컨소시엄 관리
          </button>
        </nav>
      </div>

      {/* 매칭 결과 탭 */}
      {activeTab === 'matching' && (
        <div className="space-y-4">
          {matchResults.map((match) => (
            <div key={match.id} className="card hover:shadow-medium transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-4">
                    {/* 기업 정보 */}
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center text-white font-bold">
                        {match.company.name.charAt(0)}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{match.company.name}</h3>
                        <p className="text-sm text-gray-500">{match.company.type} · {match.company.industry}</p>
                      </div>
                    </div>

                    <ArrowRight className="h-6 w-6 text-gray-300" />

                    {/* 공모사업 정보 */}
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-xl flex items-center justify-center text-white">
                        <FileText className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{match.grant.title}</h3>
                        <p className="text-sm text-gray-500">{match.grant.source} · 마감 {match.grant.deadline}</p>
                      </div>
                    </div>
                  </div>

                  {/* 매칭 사유 */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">AI 매칭 분석</h4>
                    <ul className="space-y-1">
                      {match.matchReasons.map((reason, index) => (
                        <li key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                          <span>{reason}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* 하단 정보 */}
                  <div className="flex items-center space-x-6 text-sm text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Award className="h-4 w-4" />
                      <span>선정률: {match.company.successRate}%</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <TrendingUp className="h-4 w-4" />
                      <span>예산: {match.grant.budget}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-4 ml-6">
                  {/* 매칭 점수 */}
                  <div className={`px-4 py-2 rounded-xl ${getScoreColor(match.matchScore)}`}>
                    <div className="text-center">
                      <p className="text-2xl font-bold">{match.matchScore}%</p>
                      <p className="text-xs">매칭률</p>
                    </div>
                  </div>

                  {/* 상태 */}
                  <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(match.status)}`}>
                    {match.status}
                  </span>

                  {/* 액션 버튼 */}
                  <div className="flex flex-col space-y-2">
                    <button className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors">
                      컨소시엄 구성
                    </button>
                    <button className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors">
                      상세 분석
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 컨소시엄 관리 탭 */}
      {activeTab === 'consortium' && (
        <div className="space-y-4">
          {consortiums.map((consortium) => (
            <div key={consortium.id} className="card hover:shadow-medium transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{consortium.name}</h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(consortium.status)}`}>
                      {consortium.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{consortium.grant.title}</p>
                  <p className="text-sm text-gray-500">{consortium.grant.source} · 마감 {consortium.grant.deadline}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">총 예산</p>
                  <p className="text-xl font-bold text-gray-900">{consortium.totalBudget}</p>
                </div>
              </div>

              {/* 컨소시엄 멤버 */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">참여 기관</h4>
                <div className="space-y-3">
                  {consortium.members.map((member, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-medium ${
                          member.role === '주관기관' ? 'bg-emerald-500' : 'bg-blue-500'
                        }`}>
                          {member.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{member.name}</p>
                          <p className="text-xs text-gray-500">{member.role}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="w-32">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-gray-500">참여비율</span>
                            <span className="font-medium">{member.contribution}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5">
                            <div 
                              className={`h-1.5 rounded-full ${member.role === '주관기관' ? 'bg-emerald-500' : 'bg-blue-500'}`}
                              style={{ width: `${member.contribution}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 액션 버튼 */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">생성일: {consortium.createdAt}</p>
                <div className="flex space-x-2">
                  <button className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors">
                    멤버 관리
                  </button>
                  <button className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors">
                    제안서 작성
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 컨소시엄 생성 모달 */}
      {showCreateConsortium && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">새 컨소시엄 구성</h2>
              <button 
                onClick={() => setShowCreateConsortium(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">컨소시엄명 *</label>
                <input
                  type="text"
                  placeholder="AI 스마트팜 혁신 컨소시엄"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">대상 공모사업 *</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500">
                  <option>2026 NIPA AI 융합 서비스 개발 지원</option>
                  <option>XR 콘텐츠 제작 지원사업</option>
                  <option>NIA 디지털 전환 지원사업</option>
                  <option>그린에너지 혁신 기술개발 사업</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">주관기관 *</label>
                <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500">
                  <option>(주)테크솔루션</option>
                  <option>(주)에듀테크</option>
                  <option>(주)스마트팩토리</option>
                  <option>(주)그린에너지</option>
                  <option>경북연구원</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">참여기관</label>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <select className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500">
                      <option>참여기관 선택</option>
                      <option>경북연구원</option>
                      <option>(주)농업데이터</option>
                      <option>대구대학교</option>
                    </select>
                    <input
                      type="number"
                      placeholder="참여비율 %"
                      className="w-28 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                    <button className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <button className="mt-2 flex items-center text-sm text-emerald-600 hover:text-emerald-700">
                  <Plus className="h-4 w-4 mr-1" />
                  참여기관 추가
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">총 사업비</label>
                <input
                  type="text"
                  placeholder="15억원"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button 
                onClick={() => setShowCreateConsortium(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                취소
              </button>
              <button className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors">
                <Users2 className="h-4 w-4 mr-2 inline" />
                컨소시엄 생성
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchingConsortium;

